import { Route, Routes } from 'react-router-dom';

import { Amplify } from 'aws-amplify';

import LineLayout from './layouts/line/LineLayout';
import LineStaffLayout from './layouts/line/LineStaffLayout';
import { PrivateLayout } from './layouts/PrivateLayout';
import { PublicLayout } from './layouts/PublicLayout';
import { useSession } from './layouts/SessionContext';
import { AdminTopPage } from './pages/admin';
import { DashBoardPage } from './pages/admin/dashboard';
import { PerformancesPage } from './pages/admin/reservations/children/[id]/performances';
import LoginPage from './pages/login';
import UserPage from './pages/mypage';
import { PasswordReminder } from './pages/password-reminder';
import StaffPage from './pages/staff';
import { StaffOffices } from './pages/staff/offices';
import UserContactChildrenPage from './pages/user/contact/children';
import UserContactDetailPage from './pages/user/contact/detail';
import UserContactHistoryPage from './pages/user/contact/history';
import UserPerformanceChildrenPage from './pages/user/performances';
import PerformancePage from './pages/user/performances/children/[id]';
import UserSupportPlanChildrenPage from './pages/user/support-plans';
import SupportPlanPage from './pages/user/support-plans/children/[id]';

import { AccountPage } from '@/pages/admin/accounts';
import { AchievementsUsersPage } from '@/pages/admin/achievements';
import { ChildrenPage } from '@/pages/admin/children';
import { EditChildPage } from '@/pages/admin/children/[id]';
import { ChildSupportPlansPage } from '@/pages/admin/children/[id]/support-plans';
import { LineAccountPage } from '@/pages/admin/line-accounts';
import { OfficesPage } from '@/pages/admin/offices';
import { MonthlyCalendarPage } from '@/pages/admin/reservations/calendar';
import DinnerReservationsPage from '@/pages/admin/reservations/dinner';
import { SupportRecordPage } from '@/pages/admin/reservations/support-records/[id]';
import { ReservationsTransportPage } from '@/pages/admin/reservations/transport';
import { ReservationsVisitPage } from '@/pages/admin/reservations/visit';
import { SchoolsPage } from '@/pages/admin/schools';
import { EditSupportPlanDraftPage } from '@/pages/admin/support-plan-drafts/[id]/edit';
import { CreateSupportPlanDraftPage } from '@/pages/admin/support-plan-drafts/create';
import { SupportPlans } from '@/pages/admin/support-plans';
import { EditSupportPlanPage } from '@/pages/admin/support-plans/[id]/edit';
import { CreateSupportPlanPage } from '@/pages/admin/support-plans/create';
import { TemplatesPage } from '@/pages/admin/templates';
import { Page as AdminUsersPage } from '@/pages/admin/users';
import { VehiclesPage } from '@/pages/admin/vehicles';
import { Page as ReservationPage } from '@/pages/mypage/reservations';
import { Page as NotFoundPage } from '@/pages/not-found';
import { StaffAttendanceRecordsPage } from '@/pages/staff/offices/attendance-records';
import { StaffAttendanceRecordDetailPage } from '@/pages/staff/offices/attendance-records/[id]';
import { StaffSupportRecordsPage } from '@/pages/staff/offices/support-records';
import { StaffTransportPage } from '@/pages/staff/offices/transport';

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VITE_USER_POOL_ID,
      userPoolClientId: import.meta.env.VITE_USER_POOL_CLIENT_ID,
      loginWith: {
        email: true,
      },
      signUpVerificationMethod: 'code',
      userAttributes: {
        email: {
          required: true,
        },
      },
    },
  },
});

function App(): JSX.Element {
  const { isSignedIn } = useSession();

  return (
    <Routes>
      <Route element={<LineLayout />}>
        <Route
          path="/"
          element={<UserPage />}
        />
        <Route
          path="/reservations"
          element={<ReservationPage />}
        />
      </Route>

      <Route element={<LineLayout />}>
        <Route
          path="/user/contact/children"
          element={<UserContactChildrenPage />}
        />
        <Route
          path="/user/contact/children/:childId/detail/:reservationId"
          element={<UserContactDetailPage />}
        />
        <Route
          path="/user/contact/children/:childId/history"
          element={<UserContactHistoryPage />}
        />
        <Route
          path="/user/performances"
          element={<UserPerformanceChildrenPage />}
        />
        <Route
          path="/user/performances/children/:id"
          element={<PerformancePage />}
        />
        <Route
          path="/user/support-plans"
          element={<UserSupportPlanChildrenPage />}
        />
        <Route
          path="/user/support-plans/children/:id"
          element={<SupportPlanPage />}
        />
      </Route>
      <Route element={<LineStaffLayout />}>
        <Route
          path="/staff"
          element={<StaffPage />}
        />
        <Route
          path="/staff/offices"
          element={<StaffOffices />}
        />
        <Route
          path="/staff/offices/:office_id/transport"
          element={<StaffTransportPage />}
        />
        <Route
          path="/staff/offices/:office_id/support-records"
          element={<StaffSupportRecordsPage />}
        />
        <Route
          path="/staff/offices/:office_id/attendance-records"
          element={<StaffAttendanceRecordsPage />}
        />
        <Route
          path="/staff/offices/:office_id/attendance-records/:id"
          element={<StaffAttendanceRecordDetailPage />}
        />
      </Route>

      <Route element={<PublicLayout isLogin={isSignedIn} />}>
        <Route
          path="/admin/login"
          element={<LoginPage />}
        />
        <Route
          path="/admin/password-reminder"
          element={<PasswordReminder />}
        />
      </Route>
      <Route
        path="/admin"
        element={<PrivateLayout isLogin={isSignedIn} />}
      >
        <Route
          index
          element={<AdminTopPage />}
        />
        <Route
          path="dashboard"
          element={<DashBoardPage />}
        />
        <Route
          path="reservations/visit"
          element={<ReservationsVisitPage />}
        />
        <Route
          path="reservations/transport"
          element={<ReservationsTransportPage />}
        />
        <Route
          path="reservations/dinner"
          element={<DinnerReservationsPage />}
        />
        <Route
          path="reservations/calendar"
          element={<MonthlyCalendarPage />}
        />
        <Route
          path="reservations/:id/support-records"
          element={<SupportRecordPage />}
        />
        <Route
          path="reservations/children/:id/performances"
          element={<PerformancesPage />}
        />
        <Route
          path="vehicles"
          element={<VehiclesPage />}
        />
        <Route
          path="schools"
          element={<SchoolsPage />}
        />
        <Route
          path="offices"
          element={<OfficesPage />}
        />
        <Route
          path="templates"
          element={<TemplatesPage />}
        />
        <Route
          path="users"
          element={<AdminUsersPage />}
        />
        <Route
          path="accounts"
          element={<AccountPage />}
        />
        <Route
          path="children"
          element={<ChildrenPage />}
        />
        <Route
          path="children/:id"
          element={<EditChildPage />}
        />
        <Route
          path="children/:id/support-plans"
          element={<ChildSupportPlansPage />}
        />
        <Route path="achievements">
          <Route
            path="users"
            element={<AchievementsUsersPage />}
          />
        </Route>
        <Route
          path="support-plans"
          element={<SupportPlans />}
        />
        <Route
          path="support-plans/create"
          element={<CreateSupportPlanPage />}
        />
        <Route
          path="support-plans/:id/edit"
          element={<EditSupportPlanPage />}
        />
        <Route
          path="support-plan-drafts/create"
          element={<CreateSupportPlanDraftPage />}
        />
        <Route
          path="support-plan-drafts/:id/edit"
          element={<EditSupportPlanDraftPage />}
        />
        <Route
          path="line-accounts"
          element={<LineAccountPage />}
        />
      </Route>
      <Route
        path="*"
        element={<NotFoundPage />}
      />
      <Route
        path="not-found"
        element={<NotFoundPage />}
      />
    </Routes>
  );
}

export default App;
